import { Chip, ChipGroup, type SvgComponentType } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import {
  contentTypeSvgMap,
  excludedTypes,
} from "@/search/NewSearchPageMVP/ContentTypeFilter/ContentTypeFilterChips/ContentTypeFilterChips.constants";
import {
  contentTypesChipGroupCss,
  contentTypesVisibilityCss,
  getContentTypeChipCss,
} from "@/search/NewSearchPageMVP/FilterPanel/filters/ContentTypes/ContentTypes.styles";
import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import { FilterSection } from "@/search/NewSearchPageMVP/shared/FilterSection";
import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";

const ChipWithTrackEvent = withTrackEvent(Chip);

const ContentTypes = () => {
  const {
    getters: { validQueryParams },
    handlers: {
      hasSearchParamValue,
      getSearchParamValues,
      toggleSearchParamValue,
      deleteSearchParamValues,
    },
  } = useSearchPageContext();

  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();
  const contentTypeValues =
    validQueryParams[AgathaSchemaParamsEnum.content_types]?.items?.values;

  if (!contentTypeValues) return null;

  const handleChipSelection = (_: unknown, value: string) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    if (value === "all") {
      deleteSearchParamValues(AgathaSchemaParamsEnum.content_types);
      return;
    }
    toggleSearchParamValue(AgathaSchemaParamsEnum.content_types, value);
  };

  const activeChips = getSearchParamValues(
    AgathaSchemaParamsEnum.content_types,
  );
  const isSelectAllChipActive = activeChips.length === 0;

  return (
    <div css={contentTypesVisibilityCss}>
      <FilterSection title="Content type" defaultCollapsed={false}>
        <ChipGroup
          groupName="contentTypeLevelFilter"
          __cssFor={contentTypesChipGroupCss}
          onChipEvent={handleChipSelection}
          size={ChipGroup.chipSizes.small}
          chipType={ChipGroup.chipTypes.filter}
        >
          <ChipWithTrackEvent
            __cssFor={getContentTypeChipCss(isSelectAllChipActive)}
            key="select-all-chip"
            dataProps={{
              "data-qa-selector": "select-all-content-types-filter",
              "data-testid": "select-all-content-types-filter",
              "data-is-selected": String(isSelectAllChipActive),
            }}
            active={
              !getSearchParamValues(AgathaSchemaParamsEnum.content_types).length
            }
            text="All"
            value="all"
            streamProperties={{
              description: "Select all content types search filter",
            }}
            objectType={objectTypes.content}
            actionPrefix={actionPrefixes.initiate}
            actionName={actionNames.filter}
          />

          {contentTypeValues.map(({ label, value }) => {
            if (excludedTypes.includes(value)) return null;

            const isActive = hasSearchParamValue(
              AgathaSchemaParamsEnum.content_types,
              value,
            );
            return (
              <ChipWithTrackEvent
                key={value}
                value={value}
                text={label}
                active={isActive}
                SvgComponent={contentTypeSvgMap[value] as SvgComponentType}
                objectType={objectTypes.content}
                actionPrefix={actionPrefixes.initiate}
                actionName={actionNames.filter}
                streamProperties={{
                  description: `Toggle ${label} content type search filter ${isActive ? "off" : "on"}`,
                }}
                __cssFor={getContentTypeChipCss(isActive)}
                dataProps={{
                  "data-qa-selector": `select-${label.replace(" ", "")}-search-filter`,
                  "data-testid": `select-${label.replace(" ", "")}-search-filter`,
                  "data-is-selected": String(isActive),
                }}
              />
            );
          })}
        </ChipGroup>
      </FilterSection>
    </div>
  );
};

ContentTypes.displayName = "ContentTypes";
export { ContentTypes };
